'use client'

import { PopperContentProps } from '@radix-ui/react-popper'
import * as React from 'react'
import { useEffect, useState } from 'react'

import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'

export interface SelectOption {
  label: string
  value: string
}
export interface AppSelectProps {
  label?: string
  placeholder?: string
  options: SelectOption[]
  value: string
  onChange(value: string): void
  className?: string
  disabled?: boolean
  triggerClassName?: string
  selectValueComponent?: React.ReactNode
  menuPlacement?: PopperContentProps['side']
  avoidCollisions?: boolean
  selectContentClassName?: string
  required?: boolean
  dir?: 'rtl' | 'ltr'
}
export const AppSelect = React.forwardRef<HTMLButtonElement, AppSelectProps>(
  (
    {
      label,
      placeholder,
      options,
      value,
      onChange,
      triggerClassName,
      className,
      disabled,
      selectValueComponent,
      menuPlacement,
      avoidCollisions,
      selectContentClassName,
      required,
      dir = 'rtl',
    },
    ref
  ) => {
    // State for floating label
    const [focused, setFocused] = useState(false)
    const [hasValue, setHasValue] = useState(Boolean(value))

    // Update hasValue when value changes
    useEffect(() => {
      setHasValue(Boolean(value))
    }, [value])

    // Simply ensure value is a string
    const safeValue = value || ''

    const handleOpenChange = (open: boolean) => {
      setFocused(open)
    }
    return (
      <div dir={dir} className={cn('relative space-y-2', className)}>
        <Select value={safeValue} onValueChange={onChange} disabled={disabled} onOpenChange={handleOpenChange}>
          <SelectTrigger
            ref={ref}
            className={cn(
              'text-text-secondary h-full w-full cursor-pointer rounded-full border bg-white !px-3 py-2 shadow-none',
              triggerClassName
            )}
          >
            {selectValueComponent || <SelectValue placeholder={label ? '' : placeholder || ''} />}
          </SelectTrigger>
          <SelectContent
            side={menuPlacement}
            className={cn('overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md', selectContentClassName)}
            avoidCollisions={avoidCollisions}
          >
            <SelectGroup dir='rtl'>
              {options.map(option => (
                <SelectItem key={option.value} value={option.value} className='break-words whitespace-normal'>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        {label && (
          <label
            className={cn(
              'absolute start-0 top-0 z-10 cursor-text text-sm transition-all',
              focused || hasValue ? '-top-2 text-xs text-green-600' : 'pointer-events-none top-1/2 -translate-y-1/2 text-gray-400',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {label}
            {required && !(focused || hasValue) && <span className='text-gray-400'>*</span>}
          </label>
        )}
      </div>
    )
  }
)

AppSelect.displayName = 'AppSelect'
